import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import React from 'react';

import theme from '../../theme';

// KPI Card Component
interface KPICardProps {
  title: string;
  subtitle?: string;
  metrics: { value: string; label: string }[];
}

const KPICard: React.FC<KPICardProps> = ({ title, subtitle, metrics }) => (
  <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid #EFF0F0', borderRadius: 2 }}>
    <CardContent sx={{ fontFamily: 'Manrope, sans-serif' }}>
      <Typography
        variant="inputLabel1"
        component="h3"
        gutterBottom
        sx={{ fontFamily: 'Manrope, sans-serif' }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="cardSubTitle1" display="block" mb={2}>
          {subtitle}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 4 }}>
        {metrics.map((metric, index) => (
          <Box key={index}>
            <Typography
              variant="cardTitle1"
              component="div"
              sx={{ fontSize: '28px', fontFamily: 'Manrope, sans-serif' }}
            >
              {metric.value}
            </Typography>
            <Typography variant="cardSubTitle1">{metric.label}</Typography>
          </Box>
        ))}
      </Box>
    </CardContent>
  </Card>
);

// Stacked Bar Chart Component (Simplified visual representation)
const ActivationChart: React.FC = () => {
  const weeks = [
    'Aug 24',
    'Aug 31',
    'Sep 7',
    'Sep 14',
    'Sep 21',
    'Sep 28',
    'Oct 5',
    'Oct 12',
    'Oct 19',
  ];
  const data = [
    { total: 10, babyBoomers: 2, genX: 3, millennials: 3, genZ: 2 },
    { total: 503, babyBoomers: 80, genX: 150, millennials: 180, genZ: 93 },
    { total: 800, babyBoomers: 100, genX: 200, millennials: 310, genZ: 190 },
    { total: 0, babyBoomers: 0, genX: 0, millennials: 0, genZ: 0 },
    { total: 0, babyBoomers: 0, genX: 0, millennials: 0, genZ: 0 },
    { total: 0, babyBoomers: 0, genX: 0, millennials: 0, genZ: 0 },
    { total: 0, babyBoomers: 0, genX: 0, millennials: 0, genZ: 0 },
    { total: 0, babyBoomers: 0, genX: 0, millennials: 0, genZ: 0 },
    { total: 0, babyBoomers: 0, genX: 0, millennials: 0, genZ: 0 },
  ];
  const maxValue = 800;

  const colors = {
    babyBoomers: '#FAB306',
    genX: '#EE7626',
    millennials: '#57A635',
    genZ: '#141417',
  };

  return (
    <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid #EFF0F0', borderRadius: 2 }}>
      <CardContent sx={{ fontFamily: 'Manrope, sans-serif' }}>
        <Typography
          variant="inputLabel1"
          component="h3"
          gutterBottom
          sx={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Activated Users by Generation (YTD)
        </Typography>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {Object.entries(colors).map(([key, color]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: '50%' }} />
              <Typography variant="caption">
                {key === 'babyBoomers'
                  ? 'Baby Boomers'
                  : key === 'genX'
                    ? 'Gen X'
                    : key === 'millennials'
                      ? 'Millennials'
                      : 'Gen Z'}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Chart */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 200, mb: 2 }}>
          {data.map((week, index) => (
            <Box
              key={index}
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  height: 180,
                }}
              >
                {week.total > 0 && (
                  <>
                    <Box
                      sx={{
                        height: `${(week.babyBoomers / maxValue) * 100}%`,
                        bgcolor: colors.babyBoomers,
                      }}
                    />
                    <Box
                      sx={{ height: `${(week.genX / maxValue) * 100}%`, bgcolor: colors.genX }}
                    />
                    <Box
                      sx={{
                        height: `${(week.millennials / maxValue) * 100}%`,
                        bgcolor: colors.millennials,
                      }}
                    />
                    <Box
                      sx={{ height: `${(week.genZ / maxValue) * 100}%`, bgcolor: colors.genZ }}
                    />
                  </>
                )}
              </Box>
              {week.total > 0 && (
                <Typography variant="caption" fontWeight={600} sx={{ mt: 0.5 }}>
                  {week.total}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* X-axis labels */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {weeks.map((week, index) => (
            <Box key={index} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {week}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Table */}
        <Box sx={{ mt: 3, borderTop: '1px solid #EFF0F0', pt: 2 }}>
          <Box sx={{ display: 'flex', mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Week
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Activated Users
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Activation Rate (YTD)
            </Typography>
          </Box>
          {[
            { week: 'Aug 24', users: 10, rate: '0.0%' },
            { week: 'Aug 31', users: 503, rate: '1.2%' },
            { week: 'Sep 7', users: 800, rate: '1.9%' },
          ].map((row) => (
            <Box key={row.week} sx={{ display: 'flex', py: 0.5 }}>
              <Typography variant="caption" sx={{ flex: 1 }}>
                {row.week}
              </Typography>
              <Typography variant="caption" sx={{ flex: 1 }}>
                {row.users}
              </Typography>
              <Typography variant="caption" sx={{ flex: 1 }}>
                {row.rate}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          * Of the 42,438 eligible members, 800 (1.9%) have activated their account as of Sep 4
        </Typography>
      </CardContent>
    </Card>
  );
};

// Horizontal Bar Chart Component
const ActionCompletionChart: React.FC = () => {
  const actions = [
    { label: 'Launch your news experience', percentage: 100 },
    { label: 'Trivia (Any)', percentage: 84 },
    { label: 'Health Adv. Activi...', percentage: 17 },
    { label: 'Complete the total assessment', percentage: 9 },
    { label: 'Start your wellness coaching', percentage: 3 },
    { label: 'Get your flu vacci...', percentage: 3 },
  ];

  return (
    <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid #EFF0F0', borderRadius: 2 }}>
      <CardContent sx={{ fontFamily: 'Manrope, sans-serif' }}>
        <Typography
          variant="inputLabel1"
          component="h3"
          gutterBottom
          sx={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Action Completion Rates of Activated Users (YTD)
        </Typography>

        <Box sx={{ mt: 3 }}>
          {actions.map((action, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" noWrap sx={{ maxWidth: '70%' }}>
                  {action.label}
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {action.percentage}%
                </Typography>
              </Box>
              <Box sx={{ height: 20, bgcolor: '#EFF0F0', borderRadius: 1, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${action.percentage}%`,
                    bgcolor:
                      index === 0
                        ? theme.palette.accentsYellowA.main
                        : index === 1
                          ? theme.palette.accentsOrangeB.main
                          : theme.palette.success500.main,
                    borderRadius: 1,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        {/* X-axis */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          {['0%', '20%', '40%', '60%', '80%', '100%'].map((label) => (
            <Typography key={label} variant="caption" color="text.secondary">
              {label}
            </Typography>
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          * 1,196 target actions have been completed, resulting in 1.5 actions completed per
          activated member on average.
        </Typography>
      </CardContent>
    </Card>
  );
};

// Breadcrumb Component
interface BreadcrumbProps {
  items: string[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    {items.map((item, index) => (
      <React.Fragment key={item}>
        <Typography
          variant={index === items.length - 1 ? 'breadcrumbActive' : 'breadcrumbInactive'}
          sx={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {item}
        </Typography>
        {index < items.length - 1 && <Typography variant="breadcrumbInactive">{' > '}</Typography>}
      </React.Fragment>
    ))}
  </Box>
);

const Overview: React.FC = () => {
  return (
    <Box sx={{ p: 3, bgcolor: '#F7F5F2', minHeight: '100%', fontFamily: 'Manrope, sans-serif' }}>
      <Breadcrumb items={['Sunny Benefits', 'Analytics', 'Overview']} />

      <Typography variant="title1" component="h1" gutterBottom>
        Overview
      </Typography>
      <Typography variant="cardSubTitle1" mb={3}>
        As of Sep 8, 2025
      </Typography>

      {/* KPI Cards Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <KPICard
            title="Program Activation Rates"
            subtitle="How many members have activated KP..."
            metrics={[
              { value: '800', label: 'Activated Users (YTD)' },
              { value: '1.9%', label: 'Activation Rate (YTD)' },
              { value: '+297', label: 'Newly Activated Users This Week' },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KPICard
            title="Actions Completed (YTD, excluding trivia)"
            subtitle="How many actions have been completed?"
            metrics={[
              { value: '1.5', label: 'Avg. completed actions / user' },
              { value: '1,196', label: 'Total Completed Actions' },
            ]}
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <ActivationChart />
        </Grid>
        <Grid item xs={12} md={5}>
          <ActionCompletionChart />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Overview;
